import { Center, Loader } from "@mantine/core";
import { lazy, Suspense } from "react";

import { ClientOnly } from "@mountayaapp/markdoc/renderers";

// GraphiQL pulls in Monaco and the GraphQL engine — heavy, browser-only, and
// rarely on screen. Code-split it so it loads only when a playground is rendered
// on the client.
const Client = lazy(() => import("./Client").then((m) => ({ default: m.GraphiQLClient })));

/**
 * GraphiQLProps configure the {% graphiql %} tag. `schema` is the SDL path
 * segment the explorer loads from (defaults to the Data API schema); `query`
 * optionally seeds the editor with a specific operation.
 */
interface GraphiQLProps {
  query?: string;
  schema?: string;
}

// The playground is the whole page: it fills the viewport below the 60px header
// so the IDE reads as a full-screen tool. The matching height in style.css keeps
// the loaded explorer the same size as this placeholder.
const PANEL_HEIGHT = "calc(100dvh - 60px)";

/**
 * fallback is the placeholder shown while the playground is server-rendered and
 * during the client load — a centered spinner reserving the playground's full
 * height so layout does not shift when it swaps in.
 */
const fallback = (
  <Center style={{ height: PANEL_HEIGHT }}>
    <Loader />
  </Center>
);

/**
 * GraphiQL is the {% graphiql %} renderer the documentation app injects. It is
 * import-light: the heavy client loads only in the browser, behind the
 * client-only gate and a Suspense boundary, so the static prerender ships the
 * fallback and never executes Monaco or the GraphQL engine.
 */
export const GraphiQL = ({ query, schema = "graphql/data" }: GraphiQLProps) => (
  <ClientOnly fallback={fallback}>
    <Suspense fallback={fallback}>
      <Client query={query} schema={schema} />
    </Suspense>
  </ClientOnly>
);
